import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";

/**
 * Premium Card Game.
 *
 * Mechanic: 4 cards face-down. Player flips them one by one.
 * If two cards match → WIN (the matching pair is the prize, driven by the
 *   segment probability chosen on the backend).
 * If all four differ → LOSE.
 *
 * How the 4 cards are built from the backend result:
 *   - When is_win is true: we render 2 copies of the winning segment + 2
 *     random decoy segments (from non-winning or other segments).
 *   - When is_win is false: we render 4 DIFFERENT segments (prefer non-win
 *     segments so the player clearly sees "4 different cards → lost").
 *   - We then shuffle the 4 positions deterministically using a seed.
 *
 * Props:
 *   segments: full segment list (label + color + is_winning)
 *   spinning: backend call in flight
 *   resultIndex: index returned by backend (arms the cards when set)
 *   winLabel: label of the winning segment (from result.result_label)
 *   isWin: boolean from backend
 *   onStart: () => void — triggers backend play
 *   onRevealed: () => void — called after the last card is flipped
 *   themeColor: accent color
 */
export default function CardGame({
  segments = [],
  spinning = false,
  resultIndex = null,
  winLabel = "",
  isWin = false,
  onStart,
  onRevealed,
  themeColor = "#8B5CF6",
  cardBackColor = "#1d2230",
  merchantLogo = null,
  labelFontFamily = null,
  labelFontWeight = null,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const [flipped, setFlipped] = useState([false, false, false, false]);
  const revealedRef = useRef(false);

  useEffect(() => {
    if (resultIndex != null && !armed) setArmed(true);
  }, [resultIndex, armed]);

  /* Build the 4 visible cards from the result + segment config. */
  const cards = useMemo(() => {
    if (resultIndex == null || !segments.length) return [];
    // Simple deterministic shuffle based on resultIndex
    const shuffle = (arr, seed) => {
      const a = [...arr];
      let s = seed + 1;
      for (let i = a.length - 1; i > 0; i--) {
        s = (s * 9301 + 49297) % 233280;
        const j = Math.floor((s / 233280) * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const winnerIdx = resultIndex;
    const winner = segments[winnerIdx] || segments[0];

    const others = segments.filter(
      (s, i) => i !== winnerIdx && (s.label || "") !== (winner?.label || "")
    );
    const uniqueByLabel = [];
    const seen = new Set();
    for (const s of others) {
      const key = (s.label || "").trim();
      if (!seen.has(key)) {
        uniqueByLabel.push(s);
        seen.add(key);
      }
    }

    let deck;
    if (isWin && winner) {
      // Two winner cards + two decoys (different labels)
      const decoys = shuffle(uniqueByLabel, winnerIdx).slice(0, 2);
      while (decoys.length < 2) {
        // Fallback: if there aren't enough unique segments, synthesize
        decoys.push({
          label: decoys.length ? "✦" : "★",
          color: "#CBD5E1",
          is_winning: false,
        });
      }
      deck = [winner, winner, decoys[0], decoys[1]];
    } else {
      // Four different labels — prefer non-winning to emphasize "lost"
      const loseSegs = segments.filter((s) => !s.is_winning);
      const pool = loseSegs.length >= 4 ? loseSegs : uniqueByLabel;
      const picked = shuffle(pool, winnerIdx);
      const unique = [];
      const seenLabels = new Set();
      for (const s of picked) {
        if (!seenLabels.has(s.label)) {
          unique.push(s);
          seenLabels.add(s.label);
        }
        if (unique.length === 4) break;
      }
      while (unique.length < 4) {
        unique.push({
          label: `✦${unique.length}`,
          color: "#CBD5E1",
          is_winning: false,
        });
      }
      deck = unique;
    }

    return shuffle(deck, winnerIdx + 7);
  }, [resultIndex, isWin, segments]);

  const handleFlip = useCallback(
    (i) => {
      if (!armed || revealedRef.current || disabled) return;
      setFlipped((prev) => {
        if (prev[i]) return prev;
        const next = [...prev];
        next[i] = true;
        return next;
      });
    },
    [armed, disabled]
  );

  /* Once all four flipped, notify parent after a short reveal delay. */
  useEffect(() => {
    if (armed && flipped.every(Boolean) && !revealedRef.current) {
      revealedRef.current = true;
      const ms = isWin ? 1100 : 900;
      const id = setTimeout(() => onRevealed && onRevealed(), ms);
      return () => clearTimeout(id);
    }
  }, [flipped, armed, isWin, onRevealed]);

  function handleStart() {
    if (spinning || armed || disabled) return;
    revealedRef.current = false;
    setFlipped([false, false, false, false]);
    onStart && onStart();
  }

  const allFlipped = armed && flipped.every(Boolean);
  const flippedCount = flipped.filter(Boolean).length;

  return (
    <div
      className="relative mx-auto w-full max-w-[460px]"
      data-testid="card-game-root"
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="absolute -inset-8 rounded-[40px] blur-3xl opacity-40 pointer-events-none"
        style={{
          background: `radial-gradient(closest-side, ${themeColor}55 0%, transparent 70%)`,
        }}
      />

      <div className="relative grid grid-cols-2 gap-3 sm:gap-4 p-2">
        {(armed ? cards : [0, 1, 2, 3]).map((c, i) => (
          <Card
            key={i}
            index={i}
            segment={armed ? c : null}
            armed={armed}
            flipped={armed && flipped[i]}
            onClick={() => handleFlip(i)}
            themeColor={themeColor}
            cardBackColor={cardBackColor}
            merchantLogo={merchantLogo}
            labelFontFamily={labelFontFamily}
            labelFontWeight={labelFontWeight}
            data-testid={`card-slot-${i}`}
          />
        ))}
      </div>

      {/* Overlay: loading or start */}
      {!armed && !spinning && (
        <button
          type="button"
          onClick={handleStart}
          disabled={disabled}
          data-testid="card-start-btn"
          className="absolute inset-0 flex items-center justify-center rounded-[28px] group"
          style={{ zIndex: 5 }}
        >
          <span
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-xs font-bold tracking-[0.22em] uppercase transition-transform group-hover:scale-[1.04] group-active:scale-[0.98]"
            style={{
              background:
                "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
              color: "#1a1410",
              boxShadow:
                "0 14px 36px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.25)",
            }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t("play.card_start")}
          </span>
        </button>
      )}

      {spinning && !armed && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-[28px]"
          style={{ background: "rgba(15,23,42,0.5)", zIndex: 5 }}
          data-testid="card-loading"
        >
          <div className="flex items-center gap-2.5 text-xs uppercase tracking-[0.3em] font-bold text-amber-200">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-300" />
            </span>
            {t("play.card_loading")}
          </div>
        </div>
      )}

      {armed && !allFlipped && (
        <p
          className="mt-5 text-center text-[11px] uppercase tracking-[0.26em] font-semibold text-muted-foreground"
          data-testid="card-progress"
        >
          {t("play.card_progress", { count: flippedCount })}
        </p>
      )}

      {allFlipped && (
        <p
          className="mt-5 text-center text-xs uppercase tracking-[0.22em] font-bold"
          style={{ color: isWin ? "#059669" : "#64748b" }}
          data-testid="card-result-line"
        >
          {isWin ? t("play.card_pair_found") : t("play.card_all_different")}
        </p>
      )}
    </div>
  );
}

function Card({ index, segment, armed, flipped, onClick, themeColor, cardBackColor = "#1d2230", merchantLogo = null, labelFontFamily = null, labelFontWeight = null }) {
  const color = segment?.color || "#8B5CF6";
  const label = segment?.label || "";
  const image = resolveImageUrl(segment?.image_url);

  // Derive a lighter + darker shade from the picked back color for the
  // subtle 135° gradient used on the card back.
  const tones = deriveCardBackTones(cardBackColor);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!armed || flipped}
      data-testid={`card-slot-${index}`}
      data-flipped={flipped ? "true" : "false"}
      className="relative aspect-[3/4] rounded-2xl select-none disabled:cursor-default"
      style={{ perspective: "1200px" }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700 ease-out"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Back face */}
        <div
          className="absolute inset-0 rounded-2xl border border-white/10 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.6)] overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            background: `linear-gradient(135deg, ${tones.light} 0%, ${tones.base} 50%, ${tones.dark} 100%)`,
          }}
        >
          {/* Merchant logo watermark — rendered behind all other decor */}
          {merchantLogo && (
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                opacity: 0.22,
                filter: "grayscale(1) brightness(1.4) contrast(1.1)",
                mixBlendMode: "screen",
              }}
            >
              <img
                src={merchantLogo}
                alt=""
                className="max-h-[70%] max-w-[70%] object-contain"
              />
            </div>
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, #f5d28c 0px, #f5d28c 1px, transparent 1px, transparent 9px)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-3 rounded-xl"
            style={{
              border: "1px solid rgba(212,175,55,0.28)",
              boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.08)",
            }}
          />
          {!merchantLogo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="text-3xl font-bold"
                style={{
                  color: "#f5e4bf",
                  textShadow: "0 2px 12px rgba(212,175,55,0.45)",
                  fontFamily: "Outfit, ui-sans-serif, system-ui, sans-serif",
                }}
              >
                ✦
              </div>
            </div>
          )}
        </div>

        {/* Front face */}
        <div
          className="absolute inset-0 rounded-2xl border border-white/70 dark:border-white/10 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.55)] overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: `linear-gradient(160deg, ${color}22, ${color}44)`,
          }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-2 rounded-xl"
            style={{
              border: `1px solid ${color}66`,
              boxShadow: `inset 0 0 0 1px ${color}22`,
            }}
          />
          <div className="relative h-full w-full flex flex-col items-center justify-center gap-2 px-3 text-center">
            {image ? (
              <img
                src={image}
                alt=""
                draggable={false}
                className="max-h-[55%] max-w-[82%] object-contain"
                style={{ filter: "drop-shadow(0 10px 12px rgba(0,0,0,0.28))" }}
                data-testid={`card-image-${index}`}
              />
            ) : (
            <div
              className="h-12 w-12 rounded-full flex items-center justify-center text-xl font-bold"
              style={{
                background: color,
                color: "#ffffff",
                boxShadow: `0 8px 20px -6px ${color}99`,
              }}
            >
              {(label || "?").trim().charAt(0).toUpperCase() || "?"}
            </div>
            )}
            <div
              className="text-xs sm:text-sm font-semibold leading-tight line-clamp-2 text-foreground"
              style={{
                fontFamily:
                  labelFontFamily ||
                  "Outfit, ui-sans-serif, system-ui, sans-serif",
                fontWeight: labelFontWeight || undefined,
              }}
            >
              {label}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function deriveCardBackTones(hex) {
  // Return 3 shades (light / base / dark) for the 135° card-back gradient.
  // Accepts #RRGGBB or #RGB. Falls back to the default dark palette.
  let clean = (hex || "").trim();
  if (!/^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(clean)) {
    return { light: "#2a3040", base: "#1d2230", dark: "#161a26" };
  }
  if (clean.startsWith("#")) clean = clean.slice(1);
  if (clean.length === 3) clean = clean.split("").map((c) => c + c).join("");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const shift = (delta) => {
    const toHex = (n) =>
      Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
    return `#${toHex(r + delta)}${toHex(g + delta)}${toHex(b + delta)}`;
  };
  return { light: shift(18), base: `#${clean}`, dark: shift(-22) };
}

